import Vue from 'vue';
import VueRouter from 'vue-router';
import SessionList from '../views/SessionList.vue';
import { supabase } from '@/lib/supabase';
import { isEmbedMode, isEmbedTokenUsable } from '@/lib/embedMode';
Vue.use(VueRouter);
const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        {
            path: '/login',
            name: 'login',
            component: () => import('../views/Login.vue'),
            meta: { public: true }
        },
        {
            path: '/auth/callback',
            name: 'auth-callback',
            component: () => import('../views/AuthCallback.vue'),
            meta: { public: true }
        },
        {
            // Task A6: static interstitial for tokenless top-level visits of embed URLs
            path: '/open-in-sparkco',
            name: 'open-in-sparkco',
            component: () => import('../views/OpenInSparkco.vue'),
            meta: { public: true }
        },
        {
            path: '/',
            name: 'sessions',
            component: SessionList,
            meta: { requiresAuth: true }
        },
        {
            path: '/session/:id',
            name: 'session',
            component: () => import('../views/SessionView.vue'),
            props: true,
            meta: { requiresAuth: true, allowIframe: true },
            beforeEnter: (to, from, next) => {
                console.log(`[DEBUG-2000] [router/index.ts] [beforeEnter] Navigating to session with ID: ${to.params.id}`);
                // Ensure a session ID is provided and is non-empty.
                const id = to.params.id;
                if (!id || id === '') {
                    next({ name: 'sessions' });
                }
                else {
                    next();
                }
            }
        },
        {
            path: '/artifacts',
            name: 'artifacts',
            component: () => import('../views/Artifacts.vue'),
            meta: { requiresAuth: true, allowIframe: true }
        },
        {
            path: '/artifacts/:id',
            name: 'artifact',
            component: () => import('../views/ArtifactView.vue'),
            props: true,
            meta: { requiresAuth: true, allowIframe: true },
            beforeEnter: (to, from, next) => {
                console.log(`[router/index.ts] [beforeEnter] Navigating to artifact with ID: ${to.params.id}`);
                // Ensure artifact ID is provided and matches format (a_xxxxxxxxxxxx)
                const id = to.params.id;
                if (!id || id === '' || !id.startsWith('a_')) {
                    console.log(`[router/index.ts] [beforeEnter] Invalid artifact ID format, redirecting to artifacts list`);
                    next({ name: 'artifacts' });
                }
                else {
                    next();
                }
            }
        },
        {
            path: '/organizations/manage',
            name: 'organization-management',
            component: () => import('../views/OrganizationManagement.vue'),
            meta: { requiresAuth: true }
        },
        {
            path: '/invite/:token',
            name: 'accept-invitation',
            component: () => import('../views/AcceptInvitation.vue'),
            meta: { requiresAuth: true }
        }
    ]
});
// Global navigation guard - check authentication before each route
router.beforeEach(async (to, from, next) => {
    // Check if route requires authentication
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
    const isPublicRoute = to.matched.some(record => record.meta.public);
    // Sparkco embed mode (Task A6): ?embed=1#sparkco_token=... — the sparkco token IS
    // the session; Supabase login is skipped entirely. Missing/expired token on
    // a top-level visit → static "open inside Sparkco" interstitial.
    if (isEmbedMode()) {
        if (isEmbedTokenUsable()) {
            next();
            return;
        }
        if (to.name !== 'open-in-sparkco') {
            console.log('[router] Embed mode without usable token, showing interstitial');
            next({ name: 'open-in-sparkco' });
            return;
        }
        next();
        return;
    }
    // Public routes can proceed
    if (isPublicRoute) {
        next();
        return;
    }
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (requiresAuth && !session) {
        // Not authenticated, redirect to login
        console.log('[router] User not authenticated, redirecting to login');
        next({ name: 'login', query: { redirect: to.fullPath } });
    }
    else if (to.path === '/login' && session) {
        // Already authenticated, redirect to home
        next({ name: 'sessions' });
    }
    else {
        // Proceed
        next();
    }
});
export default router;
